import { call, put, take, takeEvery, takeLatest, debounce, cancelled, select } from 'redux-saga/effects';
import { eventChannel } from 'redux-saga';
import {
  fetchNotificationsRequest,
  fetchNotificationsSuccess,
  fetchNotificationsFailure,
  fetchStatsRequest,
  fetchStatsSuccess,
  fetchStatsFailure,
  wsConnectRequest,
  wsConnectSuccess,
  wsConnectFailure,
  wsDisconnect,
  wsEventReceived,
  markAsReadRequest,
  markAsReadSuccess,
  markAsReadFailure,
  markAsUnreadRequest,
  markAsUnreadSuccess,
  markAsUnreadFailure,
  markAllAsReadRequest,
  markAllAsReadSuccess,
  markAllAsReadFailure,
  deleteNotificationRequest,
  deleteNotificationSuccess,
  deleteNotificationFailure,
  clearAllNotifications,
} from '../slices/notificationsSlice';
import { notificationsApi } from '../../api/notifications-api';
import { normalizarNotificacion } from '../../api/notificacion-del-servidor';
import { NotificationFilters } from '../../types/notification.types';
import { RootState } from '@/shared/store/store';
import { sessionService } from '@/shared/services/session.service';
import { websocketService } from '@/shared/services/websocket.service';
import {
  loginSuccess,
  verifyTokenSuccess,
  logoutSuccess,
  initializeAuth,
} from '@/features/security/sagas/auth-slice';
import {
  adminLoginSuccess,
  adminLogoutSuccess,
  adminVerifyTokenSuccess,
  adminInitializeAuth,
} from '@/features/security/sagas/admin-slice';

// WebSocket instance
const ws: WebSocket | null = null;
let wsChannel: any = null;

/**
 * Crear canal de eventos para Socket.IO
 */
function createSocketIOChannel() {
  return eventChannel((emitter) => {
    // Escuchar eventos de conexión
    const unsubscribeConnect = websocketService.on('connect', () => {
      emitter({ type: 'connected', timestamp: new Date().toISOString() });
    });

    const unsubscribeDisconnect = websocketService.on('disconnect', (reason) => {
      emitter({ type: 'disconnected', message: reason, timestamp: new Date().toISOString() });
    });

    // Escuchar eventos de notificaciones
    const unsubscribeQuoteNew = websocketService.on('quote:new-for-review', (data) => {
      emitter({
        type: 'message',
        event: 'quote:new-for-review',
        data,
        timestamp: new Date().toISOString()
      });
    });

    const unsubscribeQuoteSent = websocketService.on('quote:sent', (data) => {
      emitter({
        type: 'message',
        event: 'quote:sent',
        data,
        timestamp: new Date().toISOString()
      });
    });

    const unsubscribeQuoteAccepted = websocketService.on('quote:accepted', (data) => {
      emitter({
        type: 'message',
        event: 'quote:accepted',
        data,
        timestamp: new Date().toISOString()
      });
    });

    const unsubscribeQuoteRejected = websocketService.on('quote:rejected', (data) => {
      emitter({
        type: 'message',
        event: 'quote:rejected',
        data,
        timestamp: new Date().toISOString()
      });
    });

    const unsubscribeQuoteCancelled = websocketService.on('quote:cancelled', (data) => {
      emitter({
        type: 'message',
        event: 'quote:cancelled',
        data,
        timestamp: new Date().toISOString()
      });
    });

    // Función de limpieza
    return () => {
      unsubscribeConnect();
      unsubscribeDisconnect();
      unsubscribeQuoteNew();
      unsubscribeQuoteSent();
      unsubscribeQuoteAccepted();
      unsubscribeQuoteRejected();
      unsubscribeQuoteCancelled();
    };
  });
}

/**
 * Saga para manejar la conexión WebSocket
 */
function* handleWebSocketConnection(action: ReturnType<typeof wsConnectRequest>) {
  try {
    // Obtener información del usuario desde sessionService
    const user = sessionService.getUser();
    
    if (!user) {
      yield put(wsConnectFailure('No user session available'));
      return;
    }

    // Conectar usando el servicio de Socket.IO
    websocketService.connect();
    
    // Crear canal de eventos para escuchar eventos WebSocket
    wsChannel = yield call(createSocketIOChannel);

    yield put(wsConnectSuccess());

    // Escuchar eventos del canal
    while (true) {
      const event = yield take(wsChannel);
      yield put(wsEventReceived(event));
    }

  } catch (error) {
    yield put(wsConnectFailure(error instanceof Error ? error.message : 'Unknown error'));
  } finally {
    if (yield cancelled()) {
      if (wsChannel) {
        wsChannel.close();
        wsChannel = null;
      }
      websocketService.disconnect();
    }
  }
}

/**
 * Saga para manejar la desconexión WebSocket
 */
function* handleWebSocketDisconnection() {
  if (wsChannel) {
    wsChannel.close();
    wsChannel = null;
  }
  websocketService.disconnect();
}

/**
 * Activa la conexión de WebSocket y sincroniza notificaciones
 * cuando una sesión se establece o restaura.
 */
function* synchronizeNotificationsAfterAuth() {
  const user = sessionService.getUser();

  if (!user) {
    return;
  }

  const { wsConnected, wsConnecting } = yield select(
    (state: RootState) => ({
      wsConnected: state.notifications.wsConnected,
      wsConnecting: state.notifications.wsConnecting,
    })
  );
  if (!wsConnected && !wsConnecting) {
    yield put(wsConnectRequest());
  }

  yield put(fetchNotificationsRequest(undefined));
  yield put(fetchStatsRequest());
}

/**
 * Limpia recursos de notificaciones al cerrar sesión.
 */
function* handleAuthLogoutEvent() {
  yield put(wsDisconnect());
  yield put(clearAllNotifications());
}

/**
 * Saga para obtener notificaciones
 */
function* fetchNotificationsSaga(action: ReturnType<typeof fetchNotificationsRequest>) {
  try {
    const user = sessionService.getUser();
    if (!user) {
      yield put(fetchNotificationsFailure('No user session available'));
      return;
    }

    // Filtrar notificaciones por usuario y tenant actual
    const filters: NotificationFilters = {
      userId: user.id,
      tenantId: user.tenantId,
      limit: 50, // Limitar a 50 notificaciones más recientes
      ...(action.payload && typeof action.payload === 'object' && 'filters' in action.payload ? (action.payload.filters as NotificationFilters) : {}),
    };

    const response = yield call(notificationsApi.getNotifications, filters);

    if (response.success === false) {
      yield put(fetchNotificationsFailure(
        response.message || 'Error al cargar notificaciones'
      ));
      return;
    }

    const crudas = Array.isArray(response.data?.notifications)
      ? response.data.notifications
      : response.notifications || [];
    // GH#2674 — el API manda `isRead`, no `status`; sin esto ninguna se ve «sin leer».
    const notifications = crudas.map(normalizarNotificacion);
    const total = response.data?.total ?? response.total ?? notifications.length;

    yield put(fetchNotificationsSuccess({
      notifications,
      total,
    }));
  } catch (error) {
    // No mostrar errores en consola para 401 (no autenticado)
    if (error?.response?.status !== 401) {
      }
    yield put(fetchNotificationsFailure(
      error instanceof Error ? error.message : 'Error al cargar notificaciones'
    ));
  }
}

/**
 * Saga para obtener estadísticas de notificaciones
 */
function* fetchStatsSaga(action: ReturnType<typeof fetchStatsRequest>) {
  try {
    const user = sessionService.getUser();
    if (!user) {
      yield put(fetchStatsFailure('No user session available'));
      return;
    }

    const stats = yield call(notificationsApi.getStats);
    yield put(fetchStatsSuccess(stats));
  } catch (error) {
    // No mostrar errores en consola para 401 (no autenticado)
    if (error?.response?.status !== 401) {
      }
    yield put(fetchStatsFailure(
      error instanceof Error ? error.message : 'Error al cargar estadísticas'
    ));
  }
}

/**
 * Saga para marcar notificaciones como leídas
 */
function* markAsReadSaga(action: ReturnType<typeof markAsReadRequest>) {
  try {
    yield call(notificationsApi.markAsRead, action.payload);
    yield put(markAsReadSuccess());
  } catch (error) {
    // GH#2667 — antes el `catch` daba el éxito por hecho: la persona veía la notificación como
    // leída y el servidor no se enteraba, sin aviso. Ahora se REVIERTE la actualización optimista
    // (la lista y el contador de no leídas) y queda el error en el estado, igual que ya hace
    // `markAllAsReadSaga`. El cliente solo deja pasar el 404 de «la notificación no existe»
    // (las de prueba, GH#2666); cualquier otro fallo llega hasta aquí.
    yield put(markAsReadFailure({
      ids: action.payload,
      error: error instanceof Error ? error.message : 'Error al marcar como leídas',
    }));
  }
}

/**
 * Saga para marcar notificaciones como no leídas
 */
function* markAsUnreadSaga(action: ReturnType<typeof markAsUnreadRequest>) {
  try {
    // No llamar a la API ya que no hay endpoint para marcar como no leído
    // Solo aplicar el update optimista local
    yield put(markAsUnreadSuccess());
  } catch (error) {
    yield put(markAsUnreadFailure({
      ids: action.payload,
      error: error instanceof Error ? error.message : 'Error al marcar como no leídas'
    }));
  }
}

/**
 * Saga para marcar todas las notificaciones como leídas
 */
function* markAllAsReadSaga(action: ReturnType<typeof markAllAsReadRequest>) {
  try {
    yield call(notificationsApi.markAllAsRead);
    yield put(markAllAsReadSuccess());
  } catch (error) {
    yield put(markAllAsReadFailure(
      error instanceof Error ? error.message : 'Error al marcar todas como leídas'
    ));
  }
}

/**
 * Saga para eliminar una notificación
 */
function* deleteNotificationSaga(action: ReturnType<typeof deleteNotificationRequest>) {
  try {
    yield call(notificationsApi.deleteNotification, action.payload);
    yield put(deleteNotificationSuccess());
  } catch (error) {
    yield put(deleteNotificationFailure({
      id: action.payload,
      error: error instanceof Error ? error.message : 'Error al eliminar notificación'
    }));
  }
}

/**
 * Saga para manejar eventos WebSocket recibidos
 */
function* handleWebSocketEvent(action: ReturnType<typeof wsEventReceived>) {
  const { type: event, data } = action.payload;


  switch (event as string) {
    case 'quote:new-for-review':

      // Emitir evento para que el template lo capture
      window.dispatchEvent(new CustomEvent('quote:new-for-review', {
        detail: data
      }));

      // Refrescar la lista de notificaciones para que aparezcan en el dropdown
      yield put(fetchNotificationsRequest(undefined));
      yield put(fetchStatsRequest());

      // Mostrar notificación toast usando la API de Notification del navegador
      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification('Nueva Cotización', {
          body: data.message || `Nueva cotización de ${data.metadata?.agentName || 'un agente'}`,
          icon: '/favicon.ico',
          badge: '/favicon.ico',
        });
      }

      // Reproducir sonido de notificación (opcional)
      try {
        const audio = new Audio('/notification.mp3');
        audio.volume = 0.5;
        audio.play().catch(() => {
          // Ignorar si no se puede reproducir
        });
      } catch (error) {
        // Ignorar errores de audio
      }

      break;

    case 'quote:sent':
      window.dispatchEvent(new CustomEvent('quote:sent', { detail: data }));
      break;

    case 'quote:accepted':
      window.dispatchEvent(new CustomEvent('quote:accepted', { detail: data }));
      break;

    case 'quote:rejected':
      window.dispatchEvent(new CustomEvent('quote:rejected', { detail: data }));
      break;

    case 'quote:cancelled':
      window.dispatchEvent(new CustomEvent('quote:cancelled', { detail: data }));
      break;

    default:
  }
}

/**
 * Saga principal para notificaciones
 */
function* notificationsSaga() {
  // Integración con autenticación
  // GH#1875 — SessionInitializer despacha initializeAuth Y adminInitializeAuth en el
  // mismo tick al cargar CUALQUIER página. Con un takeLatest por acción, la sync corría
  // dos veces y salían /notifications ×2 y /notifications/stats ×2 (takeLatest cancelaba
  // la primera, pero la request ya estaba en el cable). Un solo debounce sobre las 6
  // señales de auth colapsa la ráfaga en UNA sync (toma la última señal del tick); el
  // retardo es imperceptible para las notificaciones.
  yield debounce(
    300,
    [
      loginSuccess.type,
      verifyTokenSuccess.type,
      initializeAuth.type,
      adminLoginSuccess.type,
      adminVerifyTokenSuccess.type,
      adminInitializeAuth.type,
    ],
    synchronizeNotificationsAfterAuth
  );

  yield takeLatest(logoutSuccess, handleAuthLogoutEvent);
  yield takeLatest(adminLogoutSuccess, handleAuthLogoutEvent);

  // WebSocket connection management
  yield takeLatest(wsConnectRequest, handleWebSocketConnection);
  yield takeEvery(wsDisconnect, handleWebSocketDisconnection);
  yield takeEvery(wsEventReceived, handleWebSocketEvent);
  
  // Data fetching
  yield takeLatest(fetchNotificationsRequest, fetchNotificationsSaga);
  yield takeLatest(fetchStatsRequest, fetchStatsSaga);
  
  // Actions
  yield takeLatest(markAsReadRequest, markAsReadSaga);
  yield takeLatest(markAsUnreadRequest, markAsUnreadSaga);
  yield takeLatest(markAllAsReadRequest, markAllAsReadSaga);
  yield takeLatest(deleteNotificationRequest, deleteNotificationSaga);
}

export default notificationsSaga;
